import {
  getStoredRevision,
  REVISION_HEADERS,
  setStoredRevision,
} from './revisionsKeys';
import {
  type FetchOptions,
  type RevisionType,
  ApiError,
  REVISION_TYPES,
} from './types';

const PHARMACY_API_PREFIX = '/pharmacies';

export const apiUrl = (pharmacyId: string, endpoint: string) =>
  `${PHARMACY_API_PREFIX}/${pharmacyId}${endpoint}`;

const createHeaders = (
  pharmacyId: string,
  revisionTypes: readonly RevisionType[],
  method?: string,
  body?: unknown
): HeadersInit => {
  const headers: Record<string, string> = {};

  // No Content-Type on GET: keeps cross-origin GETs "simple" (no CORS preflight)
  if (method !== 'GET' && !(body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  revisionTypes.forEach((revisionType) => {
    const storedRevision = getStoredRevision(pharmacyId, revisionType);

    if (storedRevision) {
      headers[REVISION_HEADERS[revisionType]] = storedRevision;
    }
  });

  return headers;
};

const prepareBody = (body: unknown): BodyInit | null => {
  if (body === null || body === undefined) {
    return null;
  }

  if (body instanceof FormData) {
    return body;
  }

  if (typeof body === 'string') {
    return body;
  }

  return JSON.stringify(body);
};

export const createApiClient = (baseURL: string) => {
  const send = async (
    endpoint: string,
    options: FetchOptions
  ): Promise<Response> => {
    const {
      pharmacyId,
      withoutPharmacyPrefix = false,
      ...fetchOptions
    } = options;
    const url = withoutPharmacyPrefix
      ? `${baseURL}${endpoint}`
      : `${baseURL}${apiUrl(pharmacyId, endpoint)}`;
    // Revisions are scoped to a pharmacy: no tenant, no revision to send or store
    const revisionTypes = withoutPharmacyPrefix ? [] : REVISION_TYPES;

    const headers = {
      ...createHeaders(
        pharmacyId,
        revisionTypes,
        fetchOptions.method,
        fetchOptions.body
      ),
      ...fetchOptions.headers,
    };

    const response = await fetch(url, {
      credentials: 'include',
      ...fetchOptions,
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));

      throw new ApiError(
        errorData.error || errorData.message || `Error ${response.status}`,
        response.status,
        response,
        errorData
      );
    }

    // Only successful writes refresh the stored revisions; a response only carries
    // the headers of the streams it touched, so a missing header keeps the stored one
    if (fetchOptions.method !== 'GET') {
      revisionTypes.forEach((revisionType) => {
        const revision = response.headers.get(REVISION_HEADERS[revisionType]);

        if (revision !== null) {
          setStoredRevision(pharmacyId, revision, revisionType);
        }
      });
    }

    return response;
  };

  const requestBlob = async (
    endpoint: string,
    options: FetchOptions
  ): Promise<Blob> => {
    const response = await send(endpoint, options);

    return response.blob();
  };

  const request = async <T>(
    endpoint: string,
    options: FetchOptions
  ): Promise<T> => {
    const response = await send(endpoint, options);

    if (response.status === 204) {
      return {} as T;
    }

    const contentType = response.headers.get('content-type');
    const contentLength = response.headers.get('content-length');

    if (contentLength === '0') {
      return {} as T;
    }

    const text = await response.text();

    if (!text || text.trim() === '') {
      return {} as T;
    }

    // Some legacy endpoints return JSON without an `application/json`
    // content-type, so try to parse regardless (like `response.json()` did).
    try {
      return JSON.parse(text) as T;
    } catch (error) {
      if (contentType?.includes('application/json')) {
        console.error('[API] JSON parse error:', error);
      }

      return {} as T;
    }
  };

  return {
    get: <T>(endpoint: string, options: FetchOptions) => {
      return request<T>(endpoint, { ...options, method: 'GET' });
    },
    post: <T>(endpoint: string, body: unknown, options: FetchOptions) => {
      return request<T>(endpoint, {
        ...options,
        method: 'POST',
        body: prepareBody(body),
      });
    },
    put: <T>(endpoint: string, body: unknown, options: FetchOptions) => {
      return request<T>(endpoint, {
        ...options,
        method: 'PUT',
        body: prepareBody(body),
      });
    },
    delete: <T>(endpoint: string, options: FetchOptions) => {
      return request<T>(endpoint, { ...options, method: 'DELETE' });
    },
    patch: <T>(endpoint: string, body: unknown, options: FetchOptions) => {
      return request<T>(endpoint, {
        ...options,
        method: 'PATCH',
        body: prepareBody(body),
      });
    },
    getBlob: (endpoint: string, options: FetchOptions) => {
      return requestBlob(endpoint, { ...options, method: 'GET' });
    },
    postBlob: (endpoint: string, body: unknown, options: FetchOptions) => {
      return requestBlob(endpoint, {
        ...options,
        method: 'POST',
        body: prepareBody(body),
      });
    },
  };
};

export const apiClient = createApiClient('');
